const COMMISSION = [500, 1000];
const COMMISSION_THRESHOLD = 20000;
const DELIVERY = 3390;
const DUTY_RATE = 0.1;
const VAT_RATE = 0.2;
const ELECTRO_EXCISE_RATE = 1.15;

const MIN_YEAR = 2000;
const YEAR_CHIPS_COUNT = 7;

const FORM_SELECTOR = '.js-calc-ua-simple-form';

const URL_PARAM_KEYS = ['year', 'engine', 'volume', 'price'] as const;
type URLParamKey = (typeof URL_PARAM_KEYS)[number];

type InputGroup = 'year' | 'volume' | 'price';
type EngineType = 'petrol' | 'diesel' | 'electro' | 'hybrid';
type Step3Mode = 'default' | 'electro';

type ChipOption = {
  value: string | number;
  label: string;
};

type Step3Variant = {
  title: string;
  placeholder: string;
  options: ChipOption[];
};

type Step3Config = Record<Step3Mode, Step3Variant>;

const step3Values = new WeakMap<
  HTMLFormElement,
  Partial<Record<Step3Mode, string>>
>();

interface CustomsBreakdown {
  duty: number;
  excise: number;
  vat: number;
  total: number;
}

function formatUSD(num: number): string {
  return (
    '$' +
    Math.round(num)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  );
}

const COUNT_UP_DURATION = 800;
const countUpFrames = new WeakMap<HTMLElement, number>();

function animateNumber(
  el: HTMLElement,
  to: number,
  format: (num: number) => string,
): void {
  const pending = countUpFrames.get(el);
  if (pending) cancelAnimationFrame(pending);

  const from = parseFloat(el.dataset.value ?? '') || 0;
  el.dataset.value = String(to);

  if (
    from === to ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    el.textContent = format(to);
    return;
  }

  const start = performance.now();
  const step = (now: number) => {
    const progress = Math.min((now - start) / COUNT_UP_DURATION, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    el.textContent = format(from + (to - from) * eased);

    if (progress < 1) {
      countUpFrames.set(el, requestAnimationFrame(step));
    } else {
      countUpFrames.delete(el);
    }
  };
  countUpFrames.set(el, requestAnimationFrame(step));
}

function parseNumeric(value: string | undefined): number {
  return parseFloat((value ?? '').replace(/[^0-9.]/g, ''));
}

function sanitizeDigitsOnly(input: HTMLInputElement): void {
  const digitsOnly = input.value.replace(/[^0-9]/g, '');
  if (digitsOnly !== input.value) input.value = digitsOnly;
}

function currentYear(): number {
  return new Date().getFullYear();
}

function validateYear(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;

  const year = Number(raw);
  const max = currentYear();
  if (!Number.isInteger(year)) {
    return window.theme.translations.calc_year_not_a_number;
  }
  if (year < MIN_YEAR || year > max) {
    return window.theme.translations.calc_year_out_of_range
      .replace('{min}', String(MIN_YEAR))
      .replace('{max}', String(max));
  }

  return null;
}

function createChip(group: InputGroup, option: ChipOption): HTMLButtonElement {
  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className =
    'calc-chip border border-[#EDEDED] text-steel-light bg-white text-[15px] leading-[21px] py-1.5 px-3 @[600px]:px-4 rounded-xl text-center [&.active]:border-brand-base [&.active]:text-white [&.active]:bg-brand-base';
  chip.dataset.group = group;
  chip.dataset.value = String(option.value);
  chip.textContent = option.label;
  return chip;
}

function renderChips(
  container: HTMLElement,
  group: InputGroup,
  options: ChipOption[],
): void {
  container.innerHTML = '';
  options.forEach((option) => container.appendChild(createChip(group, option)));
}

function getExciseBaseRate(engineType: EngineType, volumeCm3: number): number {
  if (engineType === 'diesel') return volumeCm3 > 3500 ? 172.5 : 86.25;
  return volumeCm3 > 3000 ? 115 : 57.5;
}

function calcExcise(
  engineType: EngineType,
  carYear: number,
  volumeOrBattery: number,
): number {
  if (engineType === 'electro') return volumeOrBattery * ELECTRO_EXCISE_RATE;

  const baseRate = getExciseBaseRate(engineType, volumeOrBattery);
  const age = Math.abs(currentYear() - carYear);
  return baseRate * (volumeOrBattery / 1000) * age;
}

function calcCustoms(
  carPrice: number,
  engineType: EngineType,
  carYear: number,
  volumeOrBattery: number,
): CustomsBreakdown {
  const duty = carPrice * DUTY_RATE;
  const excise = calcExcise(engineType, carYear, volumeOrBattery);
  const vat = (carPrice + excise + duty) * VAT_RATE;
  return { duty, excise, vat, total: duty + excise + vat };
}

function getStep3Container(form: HTMLFormElement): HTMLElement | null {
  return form.querySelector<HTMLElement>('.calc-step-volume');
}

function getStep3Config(form: HTMLFormElement): Step3Config | null {
  const container = getStep3Container(form);
  if (!container?.dataset.step3Config) return null;

  try {
    return JSON.parse(container.dataset.step3Config) as Step3Config;
  } catch {
    return null;
  }
}

function getResultPanel(form: HTMLFormElement): HTMLElement | null {
  const section = form.closest<HTMLElement>('.js-calc-ua-simple');
  return (
    section?.querySelector<HTMLElement>('.js-calc-ua-simple-result') ?? null
  );
}

function readURLParams(): Partial<Record<URLParamKey, string>> {
  const urlParams = new URLSearchParams(window.location.search);
  const params: Partial<Record<URLParamKey, string>> = {};

  URL_PARAM_KEYS.forEach((key) => {
    const value = urlParams.get(key);
    if (value) params[key] = value;
  });

  return params;
}

function writeURLParams(form: HTMLFormElement): void {
  const yearInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="year"]',
  );
  const volumeInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="volume"]',
  );
  const priceInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="price"]',
  );
  const engineBtn = form.querySelector<HTMLButtonElement>(
    '.calc-engine.active',
  );

  const params = new URLSearchParams();
  if (yearInput?.value.trim()) params.set('year', yearInput.value.trim());
  if (engineBtn?.dataset.value) params.set('engine', engineBtn.dataset.value);
  if (volumeInput?.value.trim()) params.set('volume', volumeInput.value.trim());
  if (priceInput?.value.trim()) params.set('price', priceInput.value.trim());

  const newURL = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
  window.history.replaceState({}, '', newURL);
}

function renderYearError(form: HTMLFormElement): boolean {
  const input = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="year"]',
  );
  const error = form.querySelector<HTMLElement>('[data-error="year"]');
  if (!input) return false;

  const message = validateYear(input.value);
  const hasValue = input.value.trim() !== '';
  input.classList.toggle('is-invalid', !!message && hasValue);
  if (error) error.textContent = hasValue ? (message ?? '') : '';

  return hasValue && !message;
}

function isGroupFilled(
  form: HTMLFormElement,
  group: InputGroup | 'engine',
): boolean {
  if (group === 'year') return renderYearError(form);
  if (group === 'engine')
    return form.querySelector('.calc-engine.active') !== null;

  const input = form.querySelector<HTMLInputElement>(
    `.calc-input[data-group="${group}"]`,
  );
  return (input?.value.trim() ?? '') !== '';
}

function checkComplete(form: HTMLFormElement): void {
  const submitBtn = form.querySelector<HTMLButtonElement>('.calc-submit');
  if (!submitBtn) return;

  const groups: (InputGroup | 'engine')[] = [
    'year',
    'volume',
    'price',
    'engine',
  ];
  submitBtn.disabled = !groups.every((group) => isGroupFilled(form, group));
}

function syncStep3ForEngine(
  form: HTMLFormElement,
  engineValue: string | undefined,
): void {
  const container = getStep3Container(form);
  const config = getStep3Config(form);
  const title = container?.querySelector<HTMLElement>('[data-step3-title]');
  const options = container?.querySelector<HTMLElement>('[data-step3-options]');
  const volumeInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="volume"]',
  );
  if (!container || !config || !title || !options || !volumeInput) return;

  const prevMode = container.dataset.step3Mode as Step3Mode | undefined;
  const nextMode: Step3Mode = engineValue === 'electro' ? 'electro' : 'default';
  if (nextMode === prevMode) return;

  // Engine volume and battery capacity are different units, so each mode
  // keeps its own value and restores it when the user switches back.
  const savedValues = step3Values.get(form) ?? {};
  if (prevMode) savedValues[prevMode] = volumeInput.value.trim();
  step3Values.set(form, savedValues);

  const variant = config[nextMode];
  container.dataset.step3Mode = nextMode;

  title.textContent = variant.title;
  volumeInput.placeholder = variant.placeholder;
  renderChips(options, 'volume', variant.options);

  volumeInput.value = savedValues[nextMode] ?? '';
  syncChipsWithInput(volumeInput);
}

function syncChipsWithInput(input: HTMLInputElement): void {
  const form = input.closest<HTMLFormElement>(FORM_SELECTOR);
  const group = input.dataset.group;
  if (!form || !group) return;

  const value = input.value.trim();
  form
    .querySelectorAll<HTMLButtonElement>(`.calc-chip[data-group="${group}"]`)
    .forEach((chip) => {
      chip.classList.toggle('active', chip.dataset.value === value);
    });
}

function activateChip(chip: HTMLButtonElement): void {
  const form = chip.closest<HTMLFormElement>(FORM_SELECTOR);
  const group = chip.dataset.group as InputGroup | undefined;
  if (!form || !group) return;

  const input = form.querySelector<HTMLInputElement>(
    `.calc-input[data-group="${group}"]`,
  );
  if (input) input.value = chip.dataset.value ?? '';

  form
    .querySelectorAll<HTMLButtonElement>(`.calc-chip[data-group="${group}"]`)
    .forEach((el) => {
      el.classList.toggle('active', el === chip);
    });

  checkComplete(form);
  writeURLParams(form);
}

function activateEngine(btn: HTMLButtonElement): void {
  const form = btn.closest<HTMLFormElement>(FORM_SELECTOR);
  if (!form) return;

  form
    .querySelectorAll('.calc-engine')
    .forEach((el) => el.classList.toggle('active', el === btn));

  syncStep3ForEngine(form, btn.dataset.value);
  checkComplete(form);
  writeURLParams(form);
}

function runCalculation(form: HTMLFormElement): boolean {
  const result = getResultPanel(form);
  const priceInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="price"]',
  );
  const yearInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="year"]',
  );
  const volumeInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="volume"]',
  );
  const engineBtn = form.querySelector<HTMLButtonElement>(
    '.calc-engine.active',
  );

  const carPrice = parseNumeric(priceInput?.value);
  const carYear = parseInt(yearInput?.value ?? '', 10);
  const volumeOrBattery = parseNumeric(volumeInput?.value);
  if (!result || !carPrice || !carYear || !volumeOrBattery) return false;

  const engineType =
    (engineBtn?.dataset.value as EngineType | undefined) ?? 'petrol';
  const customs = calcCustoms(carPrice, engineType, carYear, volumeOrBattery);
  const commission =
    carPrice > COMMISSION_THRESHOLD ? COMMISSION[1] : COMMISSION[0];
  const total = carPrice + commission + DELIVERY + customs.total;

  const setResult = (
    key: string,
    value: number,
    format: (num: number) => string = formatUSD,
  ) => {
    const el = result.querySelector<HTMLElement>(`[data-result="${key}"]`);
    if (el) animateNumber(el, value, format);
  };

  setResult('car_price', carPrice);
  setResult('commission', commission);
  setResult('delivery', DELIVERY, (num) =>
    window.theme.translations.calc_price_from.replace('%s', formatUSD(num)),
  );
  setResult('customs', customs.total);
  setResult('total', total);

  return true;
}

function applyURLParams(form: HTMLFormElement): void {
  const params = readURLParams();
  if (!params.year && !params.engine && !params.volume && !params.price) return;

  if (params.engine) {
    const engineBtn = form.querySelector<HTMLButtonElement>(
      `.calc-engine[data-value="${params.engine}"]`,
    );
    if (engineBtn) activateEngine(engineBtn);
  }

  const yearInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="year"]',
  );
  if (params.year && yearInput) {
    yearInput.value = params.year;
    syncChipsWithInput(yearInput);
  }

  const volumeInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="volume"]',
  );
  if (params.volume && volumeInput) {
    volumeInput.value = params.volume;
    syncChipsWithInput(volumeInput);
  }

  const priceInput = form.querySelector<HTMLInputElement>(
    '.calc-input[data-group="price"]',
  );
  if (params.price && priceInput) {
    priceInput.value = params.price;
    syncChipsWithInput(priceInput);
  }

  checkComplete(form);
  writeURLParams(form);
  runCalculation(form);
}

function initCalcInstance(form: HTMLFormElement): void {
  const yearChips = form.querySelector<HTMLElement>('.calc-year-chips');
  if (yearChips && !yearChips.childElementCount) {
    const max = currentYear();
    const years = Array.from({ length: YEAR_CHIPS_COUNT }, (_, i) => ({
      value: max - i,
      label: String(max - i),
    }));
    renderChips(yearChips, 'year', years);
  }

  const step3Container = getStep3Container(form);
  if (step3Container) step3Container.dataset.step3Mode = 'default';

  checkComplete(form);
  applyURLParams(form);
}

function handleSubmit(form: HTMLFormElement, event: SubmitEvent): void {
  event.preventDefault();

  const submitBtn = form.querySelector<HTMLButtonElement>('.calc-submit');
  if (submitBtn?.disabled) return;
  if (!runCalculation(form)) return;

  getResultPanel(form)?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
}

document
  .querySelectorAll<HTMLFormElement>(FORM_SELECTOR)
  .forEach(initCalcInstance);

document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  if (!target.closest(FORM_SELECTOR)) return;

  const chip = target.closest<HTMLButtonElement>('.calc-chip');
  if (chip) return activateChip(chip);

  const engine = target.closest<HTMLButtonElement>('.calc-engine');
  if (engine) return activateEngine(engine);
});

document.addEventListener('input', (e) => {
  const input = e.target as HTMLElement;
  if (!(input instanceof HTMLInputElement)) return;
  if (!input.classList.contains('calc-input')) return;
  if (!input.closest(FORM_SELECTOR)) return;

  const form = input.closest<HTMLFormElement>(FORM_SELECTOR);
  if (!form) return;

  sanitizeDigitsOnly(input);
  syncChipsWithInput(input);
  checkComplete(form);
  writeURLParams(form);
});

document.addEventListener('submit', (e) => {
  const form = e.target as HTMLElement;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches(FORM_SELECTOR)) return;

  handleSubmit(form, e as SubmitEvent);
});
